// Bulk edit — set one field on every selected row of a grid.
//
// Each row goes through that record's own edit (PATCH /companies|engagements|
// contacts/{id}), so a bulk change behaves exactly like editing the rows one
// by one: edit pins, audit, owner cascade, the acquired close-out and the
// usual DealCloud write-back. Stages and exits are deliberately absent — an
// exit needs its drawer's reason and close-out, which a bulk set would skip.

const { useState } = React;

const YES_NO = [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];

// kind → fields. `type`: member | industry | company | options | text | date | score.
// `key` is the PATCH field; `clearable` adds a "(clear)" choice.
const BULK_FIELDS = {
  companies: [
    { id: 'owner',    label: 'Owner',          key: 'assigned_user_id', type: 'member', scope: true },
    { id: 'industry', label: 'Industry',       key: 'industry_id',      type: 'industry' },
    { id: 'dnc',      label: 'DNC',            key: 'is_dnc',           type: 'options', options: YES_NO },
    { id: 'acquired', label: 'Acquired',       key: 'is_acquired',      type: 'options', options: YES_NO,
      warn: 'Marking a company acquired exits its live deals as “Company sold” and logs a note on each.' },
    { id: 'pipeline', label: 'Pipeline state', key: 'pipeline_state',   type: 'options',
      options: [{ value: 'active', label: 'Active' }, { value: 'lead_pool', label: 'Lead pool' }, { value: 'archived', label: 'Archived' }] },
    { id: 'hq_city',  label: 'HQ city',        key: 'hq_city',          type: 'text' },
    { id: 'hq_state', label: 'HQ state',       key: 'hq_state',         type: 'text' },
    { id: 'country',  label: 'Country',        key: 'country',          type: 'text' },
  ],
  deals: [
    { id: 'dealmaker', label: 'Dealmaker',       key: 'dealmaker_id',         type: 'member', scope: true },
    { id: 'associate', label: 'Associate',       key: 'associate_id',         type: 'member', clearable: true },
    { id: 'on_hold',   label: 'On hold',         key: 'on_hold',              type: 'options', clearable: 'Take off hold',
      options: [{ value: 'high', label: 'High' }, { value: 'low', label: 'Low' }, { value: 'repricing', label: 'Repricing' }] },
    { id: 'hot',       label: 'Hot score',       key: 'hot_score',            type: 'score', clearable: true },
    { id: 'close',     label: 'Est. close date', key: 'estimated_close_date', type: 'date', clearable: true },
    { id: 'next',      label: 'Next action date', key: 'next_action_date',    type: 'date', clearable: true },
  ],
  people: [
    { id: 'owner',     label: 'Owner',          key: 'contact_owner_id',  type: 'member', scope: true },
    { id: 'company',   label: 'Company',        key: 'company_id',        type: 'company' },
    { id: 'type',      label: 'Contact type',   key: 'contact_type',      type: 'options',
      options: ['seller', 'buyer', 'gpp', 'internal', 'professional'].map((v) => ({ value: v, label: v === 'gpp' ? 'GPP' : v[0].toUpperCase() + v.slice(1) })) },
    { id: 'dm',        label: 'Decision maker', key: 'is_decision_maker', type: 'options', options: YES_NO },
    { id: 'city',      label: 'City',           key: 'city',              type: 'text' },
    { id: 'state',     label: 'State',          key: 'state',             type: 'text' },
    { id: 'country',   label: 'Country',        key: 'country',           type: 'text' },
  ],
};

const BULK_SAVE = {
  companies: (id, data) => window.AtlasApi.updateCompany(id, data),
  deals:     (id, data) => window.AtlasApi.updateEngagement(id, data),
  people:    (id, data) => window.AtlasApi.updateContact(id, data),
};
const BULK_NOUN = { companies: ['company', 'companies'], deals: ['deal', 'deals'], people: ['person', 'people'] };
const BULK_CONCURRENCY = 4;
const CLEAR = '__clear__';

const coerce = (f, raw) => {
  if (raw === CLEAR) return null;
  if (f.type === 'options' && (raw === 'true' || raw === 'false') && f.options === YES_NO) return raw === 'true';
  if (f.type === 'score') return Number(raw);
  return raw;
};

const BulkEditDrawer = ({ open, kind, ids: selectedIds, onClose, onDone }) => {
  const fields = BULK_FIELDS[kind] || [];
  // The rows this run is for, fixed when the drawer opens — the grid clears
  // its selection and reloads once the run is done, under the open drawer.
  const [ids, setIds] = useState([]);
  const [fieldId, setFieldId] = useState(fields[0] ? fields[0].id : '');
  const [raw, setRaw] = useState('');
  const [wholeBook, setWholeBook] = useState(true);
  const [members, setMembers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [progress, setProgress] = useState(null);   // {done, total, failed: [{id, error}]}
  const f = fields.find((x) => x.id === fieldId) || fields[0];
  const [one, many] = BULK_NOUN[kind] || ['record', 'records'];
  const n = (ids || []).length;

  React.useEffect(() => {
    if (!open) return;
    setIds([...(selectedIds || [])]);
    setRaw(''); setProgress(null); setWholeBook(true);
    window.AtlasApi.listTeamMembers().then((r) => setMembers(Array.isArray(r) ? r : (r && r.items) || [])).catch(() => {});
    if (kind === 'companies') {
      window.AtlasApi.listIndustries().then((r) => setIndustries(Array.isArray(r) ? r : (r && r.items) || [])).catch(() => {});
    }
  }, [open, kind]);  // snapshot on open only

  const running = progress && progress.done < progress.total;
  const finished = progress && progress.done >= progress.total;
  const close = () => { if (running) return; onClose(); };

  const options = !f ? [] : f.type === 'member'
    ? members.filter((m) => m.active !== false).map((m) => ({ value: m.id, label: m.full_name || m.email }))
    : f.type === 'industry'
      ? industries.filter((i) => i.active !== false).map((i) => ({ value: i.id, label: i.name }))
      : f.type === 'score'
        ? Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
        : (f.options || []);
  const withClear = f && f.clearable
    ? [{ value: CLEAR, label: typeof f.clearable === 'string' ? f.clearable : '(clear)' }, ...options] : options;

  const ready = f && raw !== '' && n > 0;

  const run = async () => {
    if (!ready) return;
    const value = coerce(f, raw);
    const shown = raw === CLEAR ? (typeof f.clearable === 'string' ? f.clearable.toLowerCase() : 'cleared')
      : (withClear.find((o) => o.value === raw) || {}).label || raw;
    const ok = await window.showConfirm({
      title: `Set ${f.label} on ${n.toLocaleString()} ${n === 1 ? one : many}?`,
      body: `${f.label} → ${shown}.` + (f.warn && value === true ? ` ${f.warn}` : ''),
      confirmLabel: 'Apply',
      danger: Boolean(f.warn && value === true),
    });
    if (!ok) return;
    const data = { [f.key]: value };
    if (f.scope && value) data.owner_scope = wholeBook ? 'all' : 'record';
    const queue = [...ids];
    const failed = [];
    let done = 0;
    setProgress({ done: 0, total: queue.length, failed });
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        try { await BULK_SAVE[kind](id, data); } catch (e) { failed.push({ id, error: e.message }); }
        done += 1;
        setProgress({ done, total: ids.length, failed: [...failed] });
      }
    };
    await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, queue.length) }, worker));
    pushToast({
      type: failed.length ? 'warning' : 'success',
      title: `${f.label} set on ${(ids.length - failed.length).toLocaleString()} ${ids.length - failed.length === 1 ? one : many}`,
      body: failed.length ? `${failed.length} failed: ${failed[0].error}` : undefined,
    });
    if (onDone) onDone();
  };

  const input = !f ? null : (f.type === 'text' || f.type === 'date') ? (
    <input type={f.type === 'date' ? 'date' : 'text'} value={raw === CLEAR ? '' : raw}
      onChange={(e) => setRaw(e.target.value)} disabled={running}
      style={{ padding: '6px 10px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 6,
               fontFamily: 'inherit', background: 'var(--surface)', color: 'var(--text)' }} />
  ) : f.type === 'company' ? (
    <Combobox value={raw} onChange={(e) => setRaw(e.target.value)} searchable
      loadOptions={window.searchCompanyOptions} placeholder="Search companies…" disabled={running} />
  ) : (
    <Combobox value={raw} onChange={(e) => setRaw(e.target.value)} options={withClear}
      searchable={withClear.length > 8} placeholder="Choose…" disabled={running} />
  );

  return (
    <Drawer open={open} onClose={close} width={440} title={`Edit ${n.toLocaleString()} ${n === 1 ? one : many}`}
      footer={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          {progress && (
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {progress.done.toLocaleString()} / {progress.total.toLocaleString()}
              {progress.failed.length ? ` · ${progress.failed.length} failed` : ''}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {finished ? (
            <Btn size="sm" variant="accent" onClick={close}>Done</Btn>
          ) : (
            <>
              <Btn size="sm" variant="outline" disabled={running} onClick={close}>Cancel</Btn>
              <Btn size="sm" variant="accent" disabled={!ready || running} onClick={run}>
                {running ? 'Saving…' : 'Apply'}
              </Btn>
            </>
          )}
        </div>
      )}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Field">
          <Combobox value={fieldId} disabled={running}
            onChange={(e) => { setFieldId(e.target.value); setRaw(''); }}
            options={fields.map((x) => ({ value: x.id, label: x.label }))} />
        </Field>
        {f && <Field label={`New ${f.label.toLowerCase()}`}>{input}</Field>}
        {f && f.type === 'date' && f.clearable && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
            <input type="checkbox" checked={raw === CLEAR} disabled={running}
              onChange={(e) => setRaw(e.target.checked ? CLEAR : '')} style={{ accentColor: 'var(--accent)' }} />
            Clear the date instead
          </label>
        )}
        {f && f.scope && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}
            title="Off: only these records change. On: their contacts and live deals move with them (one owner per book).">
            <input type="checkbox" checked={wholeBook} disabled={running}
              onChange={(e) => setWholeBook(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
            Move the whole book with it
          </label>
        )}
        {f && f.warn && raw === 'true' && (
          <div style={{ fontSize: 12, color: 'var(--amber-fg, #b54708)', lineHeight: 1.5 }}>{f.warn}</div>
        )}
        {progress && progress.failed.length > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--red-fg, #b42318)', lineHeight: 1.5 }}>
            {progress.failed.slice(0, 5).map((x) => <div key={x.id}>{x.error}</div>)}
            {progress.failed.length > 5 && <div>…and {progress.failed.length - 5} more</div>}
          </div>
        )}
      </div>
    </Drawer>
  );
};

window.BulkEditDrawer = BulkEditDrawer;
